import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { PieChart, pieArcClasses } from "@mui/x-charts/PieChart";
import { getFireClassColor, getZoneCoverageColor } from "./riskChartColors";

// Custom tooltip: the pie formatter spreads our extra fields (comment, share)
// onto each defaultized data point, so we can read them straight off the series.
function PieItemTooltip({ itemData, series }) {
  const item = series?.data?.[itemData?.dataIndex];
  if (!item) return null;

  return (
    <Box
      sx={{
        maxWidth: 260,
        p: 1,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 3,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: item.color,
            flex: "0 0 auto",
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {item.label}
        </Typography>
        {item.share ? (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", ml: "auto" }}
          >
            {item.share}
          </Typography>
        ) : null}
      </Stack>
      {item.comment ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            color: "text.secondary",
            lineHeight: 1.4,
          }}
        >
          {item.comment}
        </Typography>
      ) : null}
    </Box>
  );
}

const resolveColor = (slice, colorMode, riskType) =>
  colorMode === "fireClass"
    ? getFireClassColor(slice.classId)
    : getZoneCoverageColor(slice.bucket, riskType);

export default function RiskPieChart({
  slices,
  colorMode,
  riskType,
  height = 160,
  innerRadius = 24,
  showLegend = true,
}) {
  const data = useMemo(
    () =>
      slices.map((slice) => ({
        id: slice.id,
        value: slice.value,
        label: slice.label,
        color: resolveColor(slice, colorMode, riskType),
        comment: slice.comment,
        share: slice.share,
      })),
    [slices, colorMode, riskType],
  );

  if (!data.length) return null;

  return (
    <Box>
      <Box sx={{ height, width: "100%" }}>
        <PieChart
          series={[
            {
              data,
              innerRadius,
              paddingAngle: 1,
              cornerRadius: 2,
              highlightScope: { faded: "global", highlighted: "item" },
              highlighted: { additionalRadius: 4 },
              faded: { innerRadius, additionalRadius: -4, color: "gray" },
            },
          ]}
          margin={{ top: 8, bottom: 8, left: 8, right: 8 }}
          sx={{
            // x-charts brightens the hovered arc (brightness(120%)); keep the
            // slice's real color and let size/fading carry the highlight.
            [`& .${pieArcClasses.highlighted}`]: { filter: "none" },
          }}
          slots={{ itemContent: PieItemTooltip }}
          slotProps={{ legend: { hidden: true } }}
        />
      </Box>

      {/* Custom legend with per-slice share. */}
      {showLegend ? (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {data.map((item) => (
            <Stack
              key={item.id}
              direction="row"
              alignItems="center"
              spacing={0.75}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.15)",
                  flex: "0 0 auto",
                }}
              />
              <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
                {item.label}
              </Typography>
              {item.share ? (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  {item.share}
                </Typography>
              ) : null}
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
